import { Directive, Injectable, inject } from '@angular/core';

import { EMPTY, Subject } from 'rxjs';

import type {
  ConfigPlugins,
  EditorButton,
  IProsemirrorConfig,
  PluginsButtons,
} from '@core/prosemirror-legacy';
import {
  AutoLinkWrapPlugin,
  BasicPlugin,
  BoldPlugin,
  ContextMenuPlugin,
  EmojiEditorPlugin,
  ItalicPlugin,
  LinkEditPlugin,
  ListPlugin,
  RtSchema,
  SCHEMAS,
  StrikethroughPlugin,
  UnderlinePlugin,
} from '@core/prosemirror-legacy';
import { ProsemirrorBaseConfig, ScrollStream$ } from '@core/prosemirror-legacy/angular';
import { SymbolPickerService } from '@core/symbol-legacy';

import { PlaceholderDropdownService } from '../components/placeholder/placeholder-dropdown/placeholder-dropdown.service';
import { ToolbarService } from '../components/toolbar/toolbar.service';
import { ContextToolbarService } from '../components/tooltips/context-toolbar/context-toolbar.service';
import { LinkComponentService } from '../components/tooltips/link/link-component.service';

@Injectable()
export class ProsemirrorViewboxConfig extends ProsemirrorBaseConfig implements IProsemirrorConfig {
  protected readonly symbolPickerService? = inject(SymbolPickerService, { optional: true });
  protected readonly toolbarService? = inject(ToolbarService, { optional: true });
  protected readonly contextMenuService? = inject(ContextToolbarService, { optional: true });
  protected readonly linkService? = inject(LinkComponentService, { optional: true });
  protected readonly scrollStream$? = inject(ScrollStream$, { optional: true });
  protected readonly placeholderDropdownService? = inject(PlaceholderDropdownService, {
    optional: true,
  });

  public eventStream$ = new Subject<void>();

  private scroll$ = this.scrollStream$ || EMPTY;

  public getSchema() {
    return SCHEMAS[RtSchema.Default](document, {
      checklistViewOnly: true,
      mentionViewOnly: true,
    });
  }

  public getRenderPlugins() {
    return [];
  }

  public getEditorPlugins() {
    return [
      new BasicPlugin(),
      new ContextMenuPlugin(this.contextMenuService!, this.scroll$),
      new EmojiEditorPlugin(this.symbolPickerService!, this.scroll$),

      new BoldPlugin(),
      new ItalicPlugin(),
      new StrikethroughPlugin(),
      new UnderlinePlugin(),
      new LinkEditPlugin(this.scroll$, this.linkService!),
      new AutoLinkWrapPlugin(),
      new ListPlugin(),
    ];
  }

  public registerToolbarButtons(buttons: PluginsButtons<ConfigPlugins<ProsemirrorViewboxConfig>>) {
    const ctxButtons = [
      buttons.toggleBold,
      buttons.toggleItalic,
      buttons.toggleUnderline,
      buttons.toggleLink,
    ];
    const leftButtons = [
      [
        buttons.toggleBold,
        buttons.toggleItalic,
        buttons.toggleUnderline,
        buttons.toggleBulletList,
        buttons.toggleOrderedList,
        buttons.toggleLink,
      ],
    ];

    const rightButtons: EditorButton[] = [buttons.toggleEmojiList];
    this.toolbarService!.setContextButtons(ctxButtons);
    this.toolbarService!.setLeftButtons(leftButtons);
    this.toolbarService!.setRightButtons(rightButtons);
  }
}

@Directive({
  selector: '[prosemirrorViewboxConfig]',
  providers: [ProsemirrorViewboxConfig],
  exportAs: 'prosemirrorConfig',
  standalone: true,
})
export class ProsemirrorViewboxConfigDirective {
  public readonly config = inject(ProsemirrorViewboxConfig);
}
